import { ArticleTradeReferences } from "@bundles/parts/components/ListV2/ContextProvider/TradeReferences/useTradeReferences"
import { addAdditionalRequests, loadErpInformation, prepareRequests, useDefaultErpSystem } from "@bundles/parts/components/ListV2/hooks"
import { ErpInfosData } from "@bundles/parts/components/ListV2/models"
import { useTelesalesCustomerNumber } from "@tm/context-distribution"
import { Article } from "@tm/models"
import { useErpConfig } from "@tm/utils"
import { useEffect, useMemo } from "react"
import { useCarouselConfigurationContext } from ".."

const DEFAULT_PAGE_SIZE = 25

type UseErpInfosProps = {
    articles: Article[]
    erpInfos: ErpInfosData["erpInfos"]
    setErpInfos: ErpInfosData["setErpInfos"]
    isEnabled: boolean
    tradeReferences?: ArticleTradeReferences[]
}

export function useErpInfos({ articles, isEnabled, tradeReferences, erpInfos, setErpInfos }: UseErpInfosProps): ErpInfosData {
    const { avoidErpRequestsForcedByDivisionChange } = useCarouselConfigurationContext()
    const { telesalesCustomerNo } = useTelesalesCustomerNumber()
    const defaultErpSystem = useDefaultErpSystem()
    const erpConfig = useErpConfig()
    const { erpSystemConfigs, useOrderByDistributor } = erpConfig

    useEffect(
        () => prepareRequests({ articles, isEnabled, tradeReferences, setErpInfos }, defaultErpSystem),
        [isEnabled, defaultErpSystem, articles, setErpInfos, tradeReferences]
    )

    // creates the erp request for the alternative erp system, when MRE is active

    // add extra erp request for the alternative erp system, when MRE is active. MRE is active when useOrderByDistributor is true.
    useEffect(
        () => addAdditionalRequests({ articles, tradeReferences, setErpInfos }, useOrderByDistributor, erpSystemConfigs),
        [tradeReferences, useOrderByDistributor, articles, erpSystemConfigs, setErpInfos]
    )

    useEffect(
        () =>
            loadErpInformation(
                erpInfos,
                setErpInfos,
                DEFAULT_PAGE_SIZE,
                defaultErpSystem,
                erpSystemConfigs,
                undefined,
                telesalesCustomerNo,
                undefined,
                avoidErpRequestsForcedByDivisionChange
            ),
        [defaultErpSystem, erpInfos, telesalesCustomerNo, setErpInfos, erpSystemConfigs, avoidErpRequestsForcedByDivisionChange]
    )

    useEffect(
        function resetErpInfos() {
            return () => setErpInfos([])
        },
        [setErpInfos]
    )

    return useMemo(
        () => ({
            isReady:
                !erpSystemConfigs?.length ||
                (!!articles.length && !!erpInfos.some((x) => x.state === "success" || x.state === "error" || x.state === "notRequested")),
            erpInfos,
            setErpInfos,
            erpConfig,
        }),
        [articles, erpInfos, erpSystemConfigs, erpConfig]
    )
}
